// react
import { FormEvent, useContext, useState } from 'react';

// types
import { ErrorCause, Feedback, TransactionType } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// mui
import { Alert, AlertTitle, Grid } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';
import { Select } from '../../fields/Select/Select';

// unions
import { transactionTypesIlap } from '../../../@types/unions';

// utils
import { translateTransactionType } from '../../../utils/translations';

// props
export interface FormAddCashInMethodProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export const FormAddCashInMethod = ({
	onClose,
	onSuccess,
	setIsLoading,
}: FormAddCashInMethodProps) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [feedback, setFeedback] = useState<Feedback | null>(null);
	const [values, setValues] = useState({
		name: '',
		transactionTypes: [] as TransactionType[],
	});

	// vars
	const idForm = 'form-add-cash-in-method';

	// transaction types options
	const transactionTypeOptions = transactionTypesIlap.map((type) => ({
		label: translateTransactionType(type, t),
		value: type,
	}));

	// handle submit
	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		setFeedback(null);
		setIsLoading?.(true);

		try {
			const res = await fetch(`${process.env.REACT_APP_API_URL}/cash-in-methods`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					'Content-Type': 'application/json',
					User: String(idToken),
				},
				body: JSON.stringify({
					name: values.name,
					project: project?.id,
					transactionTypes: values.transactionTypes,
				}),
			});

			const result = await res.json();

			if (result.error) {
				const feedbackNew = {
					alert: {
						children: result.message || t('error.default'),
					},
				};
				setFeedback(feedbackNew);
				return;
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.cashInMethodCreated', { name: values.name }),
				severity: 'success',
			});

			// call success callback
			onSuccess?.();

			// close dialog
			onClose?.();
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'CASH_IN_METHOD_UNAUTHORIZED') {
					feedbackNew.alert.children = t('error.unauthorized');
				}
			}

			setFeedback(feedbackNew);
		} finally {
			setIsLoading(false);
		}
	};
	return (
		<form id={idForm} name="formAddCashInMethod" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						required={true}
						value={values.name}
						onChange={(e) => setValues((prev) => ({ ...prev, name: e.target.value }))}
					/>
				</Grid>
				<Grid item xs={12}>
					<Select
						id={`${idForm}-transaction-types`}
						label={t('transactionTypes')}
						multiple={true}
						name="transactionTypes"
						options={transactionTypeOptions}
						value={values.transactionTypes}
						onChange={(e) =>
							setValues((prev) => ({
								...prev,
								transactionTypes: e.target.value as TransactionType[],
							}))
						}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
